import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/base/Seo";
import PageHero from "@/components/feature/PageHero";
import PrincipleGrid from "@/components/feature/PrincipleGrid";
import FaqSection from "@/components/feature/FaqSection";
import AboutIntro from "@/pages/about/components/AboutIntro";
import TestimonialSection from "@/pages/about/components/TestimonialSection";
import ContactForm from "@/pages/about/components/ContactForm";
import { siteInfo } from "@/mocks/site";
import { faqs } from "@/mocks/faqs";

const ORIGIN = "https://laisih.cn";

const CONTACT_ITEMS = [
  {
    icon: "ri-mail-send-line",
    label: "线索投递邮箱",
    value: siteInfo.cooperation,
  },
  {
    icon: "ri-customer-service-2-line",
    label: "编辑部联系邮箱",
    value: siteInfo.email,
  },
  {
    icon: "ri-time-line",
    label: "响应说明",
    value: "工作日 24 小时内查看，需核实的线索可能更久",
  },
  {
    icon: "ri-share-line",
    label: "转载与授权",
    value: "注明出处并附原文链接即可转载，请勿改写结论",
  },
];

export default function About() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于黑瓜爆料",
      url: `${ORIGIN}/about`,
      inLanguage: "zh-CN",
      description:
        "了解黑瓜爆料的品牌定位、发展理念、内容原则、用户口碑与联系方式。我们以事件时间线的方式持续追踪每一条娱乐线索。",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "黑瓜爆料编辑部",
      alternateName: siteInfo.name,
      url: ORIGIN,
      email: siteInfo.email,
      foundingDate: siteInfo.since,
      description: siteInfo.description,
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "线索投递",
          email: siteInfo.cooperation,
          availableLanguage: ["zh-CN"],
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: ORIGIN },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${ORIGIN}/about` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="关于我们｜黑瓜爆料 事件追踪的定位与内容原则"
        description="了解黑瓜爆料的品牌定位、发展理念、内容原则、用户口碑与联系方式。我们为每一条娱乐线索建立独立时间线，如实标注信源状态与当事人回应，持续跟进反转与最新进展。"
        keywords="黑瓜爆料,关于我们,内容原则,事件追踪,信源核实,线索投递"
        path="/about"
        jsonLd={jsonLd}
      />

      <PageHero
        kicker="ABOUT HEIGUA"
        title="关于黑瓜爆料"
        desc="一个以「后续」为主线的娱乐事件追踪团队。我们不追数量，只保证每一条建档的线索都能被读完。"
        crumbs={[{ label: "首页", href: "/" }, { label: "关于我们" }]}
        aside={
          <div className="flex gap-px overflow-hidden rounded-lg border border-background-300 bg-background-300">
            <div className="bg-background-50 px-4 py-3 md:px-5">
              <p className="font-heading text-xl font-bold text-primary-600 md:text-2xl">
                {siteInfo.since}
              </p>
              <p className="font-label text-xs text-foreground-500">开始追踪</p>
            </div>
            <div className="bg-background-50 px-4 py-3 md:px-5">
              <p className="font-heading text-xl font-bold text-foreground-900 md:text-2xl">
                24h
              </p>
              <p className="font-label text-xs text-foreground-500">持续跟进</p>
            </div>
          </div>
        }
      />

      <AboutIntro />
      <PrincipleGrid
        kicker="内容原则"
        title="四条能被检验的底线"
        desc="原则只有在被具体要求约束时才有意义。以下四条，是我们决定一条信息能否进入时间线的判断依据。"
      />
      <TestimonialSection />

      <section id="contact" className="scroll-mt-28 bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-[1360px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-4">
              <p className="mb-3 flex items-center gap-2 font-label text-xs font-medium uppercase tracking-[0.22em] text-primary-600">
                <span className="block h-px w-7 bg-primary-500" aria-hidden="true" />
                联系方式
              </p>
              <h2 className="mb-3 font-heading text-xl font-bold text-foreground-950 md:text-3xl">
                有话直接说，线索直接投
              </h2>
              <p className="mb-6 text-sm leading-relaxed text-foreground-600 md:text-base">
                我们不看态度，只看材料。把时间点、原始出处和你能够确认的细节写清楚，比任何情绪化的描述都有用。
              </p>
              <ul className="flex flex-col gap-3">
                {CONTACT_ITEMS.map((item) => (
                  <li
                    key={item.label}
                    className="flex items-start gap-3.5 rounded-lg border border-background-300 bg-background-50 p-4"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-label text-xs text-foreground-500">
                        {item.label}
                      </span>
                      {item.label.includes("邮箱") ? (
                        <a
                          href={`mailto:${item.value}`}
                          className="block break-all font-label text-sm text-foreground-900 transition-colors hover:text-primary-600"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <span className="block text-sm leading-relaxed text-foreground-800">
                          {item.value}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-8">
              <h3 className="mb-4 font-heading text-lg font-bold text-foreground-950">
                投递一条线索
              </h3>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <FaqSection
        kicker="常见问题"
        title="在投递之前，先看看这些"
        desc="关于信源核实、回应状态与转载授权，读者最常问的几件事都整理在这里。"
      />
    </SiteLayout>
  );
}